import React, { createContext, useCallback, useContext, useState } from 'react';
import { assertContext } from '@gopeerproject/ui-kit';
import { CONNECTION_OPTIONS } from '../constants';
import { AttachVisibilityHandler } from './AttachVisibilityHandler';
import { useLocalTracks, useRoom, useHandleRoomDisconnection, useScreenShareToggle, useActiveAudioDevice, useHandleTrackPublicationFailed, useOnRoomReconnecting, useRestartAudioTrackOnDeviceChange } from './hooks';
import { ParticipantsProvider } from './ParticipantsProvider';
import { SelectedParticipantProvider } from './SelectedParticipantProvider';
export const MediaContext = createContext(null);
export const MediaProvider = ({ children, onError, id, onRoomConnected, onRoomDisconnected, enableBackgrounds, selectedBackground, onBackgroundLoading }) => {
    const { handleConnectionError, handleReconnectionError, handleDisconnectionError, handlePublicationError, handleScreenShareError, error, setError } = useErrorHandling(onError);
    const [activeAudioDevice, setActiveAudioDevice] = useActiveAudioDevice();
    const { localTracks, getLocalAudioTrack, getLocalVideoTrack, isAcquiringLocalTracks, removeLocalVideoTrack, removeLocalAudioTrack, getAudioAndVideoTracks, acquireLocalTracks } = useLocalTracks({
        id,
        enableBackgrounds,
        onBackgroundLoading,
        selectedBackground
    });
    const { room, isConnecting, connect } = useRoom(localTracks, {
        onConnected: onRoomConnected,
        onDisconnected: onRoomDisconnected,
        onError: handleConnectionError
    }, CONNECTION_OPTIONS);
    const [isSharingScreen, toggleScreenShare] = useScreenShareToggle(room, id, handleScreenShareError);
    useOnRoomReconnecting(room, handleReconnectionError);
    useHandleTrackPublicationFailed(room, handlePublicationError);
    useHandleRoomDisconnection(room, handleDisconnectionError, removeLocalAudioTrack, removeLocalVideoTrack, isSharingScreen, toggleScreenShare);
    useRestartAudioTrackOnDeviceChange(localTracks);
    return (React.createElement(MediaContext.Provider, { value: {
            error,
            setError,
            getAudioAndVideoTracks,
            getLocalAudioTrack,
            getLocalVideoTrack,
            isAcquiringLocalTracks,
            removeLocalVideoTrack,
            removeLocalAudioTrack,
            localTracks,
            connect,
            isConnecting,
            room,
            isSharingScreen,
            toggleScreenShare,
            activeAudioDevice,
            setActiveAudioDevice,
            acquireLocalTracks
        } },
        React.createElement(SelectedParticipantProvider, { room: room },
            React.createElement(ParticipantsProvider, null, children)),
        React.createElement(AttachVisibilityHandler, null)));
};
const useErrorHandling = (onError) => {
    const [error, _setError] = useState(null);
    const handleError = useCallback(({ error, type }) => {
        onError(error, type);
    }, [onError]);
    const setError = useCallback((error) => {
        handleError({ error, type: 'generic' });
        _setError(error);
    }, [handleError, _setError]);
    const handleConnectionError = useCallback((error) => {
        handleError({ type: 'connection', error });
    }, [handleError]);
    const handleReconnectionError = useCallback((error) => {
        handleError({ type: 'reconnection', error });
    }, [handleError]);
    const handleDisconnectionError = useCallback((error) => {
        handleError({ type: 'disconnection', error });
    }, [handleError]);
    const handlePublicationError = useCallback((error) => {
        handleError({ type: 'publication', error });
    }, [handleError]);
    const handleScreenShareError = useCallback((error) => {
        handleError({ type: 'screenshare', error });
    }, [handleError]);
    return {
        handleError,
        handleConnectionError,
        handleReconnectionError,
        handleDisconnectionError,
        handlePublicationError,
        handleScreenShareError,
        error,
        setError
    };
};
export const useMediaContext = () => {
    return assertContext(useContext(MediaContext), 'useMediaContext', 'MediaProvider');
};
//# sourceMappingURL=MediaProvider.js.map